import { Injectable, Inject, Optional, InjectionToken } from '@angular/core';
import { NetworkService, API_BASE_URL } from '@angular-workspace/core';

/**
 * Injection token for roles API base path
 */
export const ROLES_API_PATH = new InjectionToken<string>('ROLES_API_PATH');

/**
 * Interface for roles data response
 */
export interface RolesResponse {
  [key: string]: any;
}

/**
 * Role types in the system
 */
export enum Role {
  ADMIN = 'admin',
  ORG_ADMIN = 'org-admin',
  PROVIDER = 'provider',
  CONSUMER = 'consumer',
}

/**
 * Role priority mapping (higher number = higher priority)
 */
export const ROLE_PRIORITY: Record<string, number> = {
  [Role.ADMIN]: 4,
  [Role.ORG_ADMIN]: 3,
  [Role.PROVIDER]: 2,
  [Role.CONSUMER]: 1,
};

/**
 * Role to panel route mapping
 */
export const ROLE_PANEL_ROUTES: Record<string, string> = {
  [Role.ADMIN]: '/admin/home',
  [Role.ORG_ADMIN]: '/org-admin/home',
  [Role.PROVIDER]: '/provider/home',
  [Role.CONSUMER]: '/consumer/home',
};

/**
 * Role alias mapping - maps API role variants to standard roles
 * This handles cases where API returns different role names (e.g., "cos_admin" -> "admin")
 */
export const ROLE_ALIASES: Record<string, string> = {
  'cos-admin': Role.ADMIN,
  'cos_admin': Role.ADMIN,
  'cosadmin': Role.ADMIN,
  'platform-admin': Role.ADMIN,
  'platform_admin': Role.ADMIN,
  'platformadmin': Role.ADMIN,
};

/**
 * Roles API Service for handling roles-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class RolesApiService {
  private userRoles: string[] = [];

  constructor(
    private networkService: NetworkService,
    @Inject(API_BASE_URL) private basePathUrl: string,
    @Optional() @Inject(ROLES_API_PATH) private rolesApiPath?: string,
  ) {}

  /**
   * Get user roles from the control plane API
   * Note: The auth token is automatically added by the authTokenInterceptor
   * @returns Promise with roles data
   */
  async getRoles(): Promise<RolesResponse> {
    try {
      // 1) Try to read roles from userData in localStorage (no network call needed)
      try {
        const userDataStr = localStorage.getItem('userData');
        if (userDataStr) {
          const userData = JSON.parse(userDataStr);
          const actualUserData = userData?.result || userData;
          if (actualUserData && Array.isArray(actualUserData.roles)) {
            this.userRoles = actualUserData.roles;
            return { roles: this.userRoles };
          }
        }
      } catch (storageError) {
        console.warn('roles-api.service: Failed to read roles from localStorage userData:', storageError);
      }

      // 2) If roles API is unavailable, just return whatever roles we already have (or empty array)
      return { roles: this.userRoles };
    } catch (error) {
      console.error('Error fetching roles:', error);
      // On error, keep any roles we already have and return them instead of throwing
      return { roles: this.userRoles };
    }
  }

  /**
   * Get the highest priority role from a list of roles
   * @param roles Array of role strings
   * @returns Highest priority role or null if no valid roles
   */
  getHighestRole(roles: string[]): string | null {
    if (!roles || roles.length === 0) {
      return null;
    }

    // Find the role with the highest priority
    let highestRole: string | null = null;
    let highestPriority = 0;

    console.log('getHighestRole - Input roles:', roles);

    for (const role of roles) {
      // Normalize role: convert to lowercase and replace underscores with hyphens
      // This handles cases where API returns "org_admin" but we need "org-admin"
      let normalizedRole = role.toLowerCase().replace(/_/g, '-');
      const originalRole = normalizedRole;
      
      // Check if this role has an alias (e.g., "cos-admin" -> "admin")
      if (ROLE_ALIASES[normalizedRole]) {
        normalizedRole = ROLE_ALIASES[normalizedRole];
        console.log(`getHighestRole - Role "${originalRole}" mapped to alias "${normalizedRole}"`);
      }
      
      const priority = ROLE_PRIORITY[normalizedRole];
      console.log(`getHighestRole - Role "${normalizedRole}" (from "${role}") has priority:`, priority);

      if (priority && priority > highestPriority) {
        highestPriority = priority;
        highestRole = normalizedRole;
        console.log(`getHighestRole - New highest role: "${highestRole}" with priority ${highestPriority}`);
      }
    }

    console.log('getHighestRole - Final highest role:', highestRole);
    return highestRole;
  }

  /**
   * Get the panel route for the highest priority role
   * @param roles Array of role strings
   * @returns Panel route path or default consumer route
   */
  getHighestRolePanelRoute(roles: string[]): string {
    const highestRole = this.getHighestRole(roles);
    console.log('getHighestRolePanelRoute - Input roles:', roles);
    console.log('getHighestRolePanelRoute - Highest role:', highestRole);
    
    if (!highestRole) {
      // Default to consumer panel if no valid roles
      console.log('getHighestRolePanelRoute - No highest role found, defaulting to consumer');
      return ROLE_PANEL_ROUTES[Role.CONSUMER];
    }

    // Use the enum value to ensure correct lookup
    const roleEnum = highestRole as Role;
    const route = ROLE_PANEL_ROUTES[roleEnum] || ROLE_PANEL_ROUTES[highestRole] || ROLE_PANEL_ROUTES[Role.CONSUMER];
    console.log('getHighestRolePanelRoute - Calculated route:', route);
    return route;
  }

  /**
   * Get the panel route for the user's highest role
   * Uses stored roles from the last getRoles() call
   * @returns Panel route path or default consumer route
   */
  getUserPanelRoute(): string {
    return this.getHighestRolePanelRoute(this.userRoles);
  }

  /**
   * Check if user has a specific role
   * @param role Role to check
   * @returns true if user has the role
   */
  hasRole(role: string): boolean {
    let normalizedRole = role.toLowerCase().replace(/_/g, '-');
    
    // Check if this role has an alias
    if (ROLE_ALIASES[normalizedRole]) {
      normalizedRole = ROLE_ALIASES[normalizedRole];
    }
    
    return this.userRoles.some(r => {
      let normalizedUserRole = r.toLowerCase().replace(/_/g, '-');
      
      // Check if user role has an alias
      if (ROLE_ALIASES[normalizedUserRole]) {
        normalizedUserRole = ROLE_ALIASES[normalizedUserRole];
      }
      
      return normalizedUserRole === normalizedRole;
    });
  }

  /**
   * Get all user roles
   * @returns Array of user roles
   */
  getUserRoles(): string[] {
    return [...this.userRoles];
  }
}

